<template>
    <div>
        <div class="welcome">

            <div class="columns is-block-desktop is-block-tablet is-flex-widescreen">
                <div class="column side-panel is-6-widescreen">
                    <div class="welcome-header mt-5 pt-5 content">
                        <img v-if="enrollment.course.logo_white" :src="enrollment.course.logo_white" style="height: 90px;"/>
                        <img v-else-if="enrollment.course.logo" :src="enrollment.course.logo" style="height: 90px;"/>

                        <div class="columns is-gapless mt-6">
                            <div class="column is-narrow">
                                <i class="fad fa-fw mr-5 mt-1 fa-2x fa-chalkboard-teacher"></i>
                            </div>
                            <div class="column">
                                <h1 class="has-text-white course-title">{{ enrollment.course.title }}</h1>
                            </div>
                        </div>

                        <render-blocks v-if="enrollment.course.welcome" :blocks="enrollment.course.welcome.content" class="course-description blocks"/>
                        <div v-else-if="enrollment.course.description" v-html="enrollment.course.description" class="course-description"></div>
                    </div>
                </div>
                <div class="column main-panel">

                    <ui-card class="mb-6" v-if="enrollment.isMissingRequiredApps">

                        <div class="columns is-gapless">
                            <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-4 is-size-5 has-text-warning mt-1"></i></div>
                            <div class="column">
                                <div class="mb-3 is-size-5 has-text-weight-bold">Action Required</div>
                                <div class="mb-5">This course requires a form or application to be completed before you can begin. Review the <strong><i class="fad ml-1 fa-file-certificate mr-2"></i>Certifications & Forms</strong> to complete any requirements.</div>
                                <router-link :to="{name: 'course-applications'}">
                                    <b-button type="is-danger" expanded class="mb-3">
                                        <i class="fad fa-file-certificate mr-4"></i>View Certifications & Forms
                                    </b-button>
                                </router-link>
                            </div>
                        </div>
                    </ui-card>

                    <ui-card class="mb-6" v-if="enrollment.completed_at && enrollment.isIncompleteApps">
                        <div class="columns is-gapless">
                            <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-4 is-size-5 has-text-warning mt-1"></i></div>
                            <div class="column">
                                <div class="mb-3 is-size-5 has-text-weight-bold">Incomplete Applications or Forms</div>
                                <div class="mb-5">Your course is complete but you have incomplete applications or forms that you may wish to complete. Review the <strong><i class="fad ml-1 fa-file-certificate mr-2"></i>Certifications & Forms</strong>.</div>
                                <router-link :to="{name: 'course-applications'}">
                                    <b-button type="is-danger" expanded class="mb-3">
                                        <i class="fad fa-file-certificate mr-4"></i>View Certifications & Forms
                                    </b-button>
                                </router-link>
                            </div>
                        </div>
                    </ui-card>

                    <ui-card class="mb-6" v-if="enrollment.isCourseworkComplete && !enrollment.completed_at">
                        <div class="columns is-gapless">
                            <div class="column is-narrow"><i class="fas fa-circle-check mr-4 is-size-5 has-text-primary mt-1"></i></div>
                            <div class="column">
                                <div class="mb-3 is-size-5 has-text-weight-bold">Are you ready to complete?</div>
                                <div class="mb-3 mt-4 is-size-5">You've finished all the requirements to complete this course.</div>
                                <div class="mt-2 mb-5">You can mark this course as complete or you can review the content. Once completed you will no longer be able to make changes to your course.</div>
                                <b-button type="is-danger" expanded @click="completeCourse()" class="mb-3" :disabled="processing">
                                    <b-icon icon="check" class="mr-2"/>
                                    Click to Complete Course
                                </b-button>
                            </div>
                        </div>
                    </ui-card>

                    <ui-card>
                        <div>
                            <b-button expanded size="is-large" type="is-info" :outlined="enrollment.isCourseworkComplete" @click="beginCourse" :disabled="enrollment.isMissingRequiredApps">
                                <template v-if="enrollment.isCourseworkComplete">Review Course <i class="fal fa-arrow-right ml-3"></i></template>
                                <template v-else>
                                    <template v-if="enrollment.last_component">Continue</template>
                                    <template v-else>Begin</template>
                                    Course <i class="fal fa-arrow-right ml-3"></i>
                                </template>
                            </b-button>
                        </div>

                        <div class="mb-5 mt-6 pl-4 pr-4">
                            <b-progress type="is-success" :value="Math.round((enrollment.course.navigation.totalComplete/enrollment.course.navigation.totalLessons)*100)" size="is-small" class="mb-1"></b-progress>
                            <div class="is-size-7">
                                <strong>Course Progress</strong> {{ enrollment.course.navigation.totalComplete }} of {{ enrollment.course.navigation.totalLessons }} ({{ Math.round((enrollment.course.navigation.totalComplete / enrollment.course.navigation.totalLessons) * 100) }}%)
                            </div>
                        </div>

                        <ul class="mt-3">
                            <template v-for="(o, index) in enrollment.course.navigation.outline">
                                <template v-if="o.type === 'SECTION'">

                                    <b-collapse
                                        animation="slide"
                                        :open="enrollment.course.navigation.currentSection === index"
                                        @open="enrollment.course.navigation.currentSection = index"
                                    >
                                        <template #trigger="props">
                                            <li>
                                                <div class="columns is-gapless is-mobile">
                                                    <div class="column">
                                                        <div class="sub-header is-size-6 mb-2">{{ o.title }}</div>
                                                        <div class="has-text-weight-normal is-text-7">{{ o.totalComplete }} of {{ o.totalLessons }} completed</div>
                                                    </div>
                                                    <div class="column is-narrow has-text-right">
                                                        <i class="far dropdown-icon" :class="[(props.open) ? 'fa-angle-up' : 'fa-angle-down']"></i>
                                                    </div>
                                                </div>
                                            </li>
                                        </template>

                                        <!-- SECTION CHILDREN -->
                                        <template v-for="z in o.children">
                                            <li class="sub-header" v-if="z.type === 'HEADER'">{{ z.title }}</li>

                                            <!-- OVERVIEW-->
                                            <template v-if="z.type === 'OVERVIEW'">
                                                <router-link
                                                    :to="(enrollment.isMissingRequiredApps) ? {} : {name: 'overview', params: {sectionId: z.id}}"
                                                    custom v-slot="{ navigate, isActive }"
                                                    active-class="active"
                                                >
                                                    <li
                                                        @click="navigate"
                                                        :class="{'active': isActive}"
                                                        :style="{'background-color': (isActive) ? colors.primary + ' !important' : 'white'}"
                                                    >
                                                        <div class="columns is-gapless">
                                                            <div class="column is-narrow">
                                                                <i class="far fa-fw fa-circle-info" :style="{'color': colors.primary}"></i>
                                                            </div>
                                                            <div class="column">Overview</div>
                                                        </div>
                                                    </li>
                                                </router-link>
                                            </template>

                                            <!-- COMPONENT -->
                                            <template v-else-if="z.type === 'COMPONENT'">
                                                <li
                                                    v-if="z.closed"
                                                    @click="contentAlert"
                                                    style="background-color: white"
                                                >
                                                    <div class="columns is-gapless is-mobile">
                                                        <div class="column is-narrow">
                                                            <i class="fa-fw" :class="z.icon" :style="{'color': colors.primary}"></i>
                                                        </div>
                                                        <div class="column">{{ z.title }}</div>
                                                    </div>
                                                </li>
                                                <router-link
                                                    v-else
                                                    :to="(enrollment.isMissingRequiredApps) ? {} : {name: 'component', params: {outlineId: z.id}}"
                                                    custom v-slot="{ navigate, isActive }"
                                                    active-class="active"
                                                >
                                                    <li
                                                        @click="navigate"
                                                        :style="{'background-color': 'white'}"
                                                    >
                                                        <div class="columns is-gapless is-mobile">
                                                            <div class="column is-narrow">
                                                                <i
                                                                    class="fa-fw"
                                                                    :class="z.icon"
                                                                    :style="{'color': colors.primary}"
                                                                ></i>
                                                            </div>
                                                            <div class="column">
                                                                {{ z.title }}
                                                            </div>
                                                            <div class="column has-text-right is-narrow" v-if="isActive ||  (enrollment.last_component && z.current)">
                                                                <b-tag type="is-info"><i class="fas fa-location-dot has-text-white is-size-7 mr-2 mb-1"></i>Current Lesson</b-tag>
                                                            </div>
                                                        </div>
                                                    </li>
                                                </router-link>
                                            </template>
                                        </template>
                                    </b-collapse>
                                </template>
                            </template>
                        </ul>

                    </ui-card>
                </div>
            </div>


        </div>
    </div>
</template>

<script>

    import {client as http} from "../../http_client";
    import {mapGetters} from "vuex";

    import UiCard from "../../components/UiCard.vue";
    import RenderBlocks from "../../components/course/RenderBlocks.vue";

    export default {
        components: {UiCard, RenderBlocks},
        props: ['enrollmentId'],

        data: function() {
            return {
                processing: false
            };
        },
        computed: {
            ...mapGetters(['colors', 'enrollment']),
        },
        methods: {
            beginCourse: function() {
                if (!this.sectionId && !this.outlineId && this.enrollment.last_component) {
                    console.log('Going to last component');
                    this.$router.push({name: 'component', params: {outlineId: this.enrollment.last_component}});
                } else {
                    if (this.enrollment.course.navigation.outline[0].children[0].type === 'OVERVIEW') {
                        console.log('Going to start, overview', this.enrollment.course.navigation.outline[0].children[0]);
                        this.$router.push({name: 'overview', params: {sectionId: this.enrollment.course.navigation.outline[0].children[0].id}});
                    } else {
                        console.log('Going to start, component', this.enrollment.course.navigation.outline[0].children[0]);
                        this.$router.push({name: 'component', params: {outlineId: this.enrollment.course.navigation.outline[0].children[0].id}});
                    }
                }
            },
            contentAlert: function() {
                window.alert('This content is not available until you complete previous content.');
            },
            completeCourse: function() {
                if (this.processing) return;
                this.processing = true;
                http.post('app/enrollments/complete', {enrollment_id: this.enrollment.id}, {force: true}).then(response => {
                    this.enrollment.completed_at = new Date();
                    this.processing = false;
                }).catch(err => {
                    if (err) console.log(err);
                    this.processing = false;
                });
            }
        }
    };
</script>

<style scoped lang="scss">

    .welcome {

        .side-panel {
            padding: 0;
            position: relative;
            transition: width 100ms;
            z-index: 100;
            color: white;

            .welcome-header {
                margin-right: 4rem;

                @media (max-width: 768px) {
                    margin: 0;
                }
            }

            .course-title {
                @media (max-width: 768px) {
                    margin: 0 !important;
                    padding: 0 !important;
                }
            }

            .course-description {
                margin: 2rem 0 0 4rem;

                &.blocks {
                    margin-left: 2rem;
                }

                @media (max-width: 768px) {
                    margin: 0;
                }
            }
        }

        .main-panel {
            position: relative;

            margin: 4.5rem 0;
            padding: 2rem;

            ul {
                margin-top: 1rem;
                box-shadow: 2px 2px 20px 0 rgba(0, 0, 0, 0.1);
                border-radius: 0.5rem;

                .section-collapse {
                    //border-radius: 0.5rem;
                    border-bottom: 1px solid #f4f4f4;

                    &:last-child {
                        border-radius: 0;
                    }
                }

                li {
                    border-top: 1px solid #f4f4f4;
                    font-size: 0.85rem;
                    line-height: 1.1rem;
                    padding: 0.9rem 2rem 0.7rem 1rem;
                    color: #4a4a4a;
                    transition: all 200ms;
                    font-weight: bold;
                    cursor: pointer;

                    &.sub-header {
                        font-size: 1rem;
                        padding-right: 0.8rem;

                        &:last-child {
                            //border-radius: 0;
                            //border-bottom: 2px solid #e1e1e1;
                        }

                    }

                    &.active {
                        background-color: #336699 !important;

                        color: white;

                        .meta, i {
                            color: white !important;
                        }
                    }

                    i {
                        color: #3c6691;
                        font-size: 1.2rem;
                        vertical-align: middle;
                        transition: all 200ms;
                        margin-right: 0.9rem
                    }

                    &:last-child {
                        border-radius: 0.5rem;
                    }
                }
            }

            @media (max-width: 768px) {
                padding: 0;
            }
        }
    }
</style>
